import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatNumber } from '../admin/format';
import { MeterState, PlanLimitKey, isUnlimited, meterPercent, meterState } from './plan-limits';
import { useUpgrade } from './useUpgrade';

const BAR: Record<MeterState, string> = {
  unlimited: 'bg-foreground/20',
  ok: 'bg-foreground/70',
  near: 'bg-warning',
  at: 'bg-destructive',
};

const TEXT: Record<MeterState, string> = {
  unlimited: 'text-muted-foreground',
  ok: 'text-foreground',
  near: 'text-amber-600 dark:text-amber-500',
  at: 'text-destructive',
};

/**
 * One plan limit and how much of it is used.
 *
 * Ink while there is room, amber from 80%, red at the limit — the only colour
 * on it, and only because it means something. An "Upgrade" link sits with it
 * whenever the limit is finite, so the way past it is next to the number.
 */
export function UsageMeter({
  limitKey,
  label,
  current,
  limit,
  variant = 'block',
  showUpgrade = true,
  hint,
  className,
}: {
  /** The plan key: names the meter (FREE_PLAN.LIMITS.*) and tells the upgrade page why. */
  limitKey?: PlanLimitKey | string;
  label?: string;
  current: number | null | undefined;
  /** -1 is unlimited, 0 is not on this plan. */
  limit: number | null | undefined;
  /** `inline` is a toolbar pill the height of the buttons beside it. */
  variant?: 'block' | 'inline';
  showUpgrade?: boolean;
  /** Replaces the "N left" line while there is room. */
  hint?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const upgrade = useUpgrade();
  const used = Math.max(0, Number(current) || 0);
  const state = meterState(used, limit);
  const unlimited = isUnlimited(limit);
  const none = !unlimited && limit === 0;
  const pct = meterPercent(used, limit);
  const name = label ?? (limitKey ? t(`FREE_PLAN.LIMITS.${limitKey}`) : '');
  const value = unlimited ? t('FREE_PLAN.UNLIMITED') : t('FREE_PLAN.METER.OF', { current: formatNumber(used), limit: formatNumber(limit as number) });
  const left = unlimited ? 0 : Math.max(0, (limit as number) - used);

  const status = none
    ? t('FREE_PLAN.METER.NOT_INCLUDED')
    : state === 'at'
      ? t('FREE_PLAN.METER.AT_LIMIT')
      : state === 'near'
        ? t('FREE_PLAN.METER.NEAR_LIMIT')
        : unlimited
          ? t('FREE_PLAN.METER.USED', { current: formatNumber(used) })
          : hint ?? t('FREE_PLAN.METER.LEFT', { count: left });

  const upgradeLink = showUpgrade && !unlimited && (
    <button
      type="button"
      onClick={() => upgrade(limitKey)}
      className="shrink-0 text-xs font-medium text-foreground underline-offset-2 hover:underline"
      data-testid="usage-meter-upgrade"
    >
      {t('FREE_PLAN.UPGRADE')}
    </button>
  );

  if (variant === 'inline') {
    return (
      <div
        data-testid="usage-meter"
        data-state={state}
        title={status}
        className={cn('inline-flex h-9 items-center gap-2.5 rounded-lg border px-3 text-sm', state === 'at' && 'border-destructive/50', className)}
      >
        <span className="text-muted-foreground">{name}</span>
        <span className={cn('font-medium tabular-nums', TEXT[state])}>{value}</span>
        {!unlimited && (
          <span className="hidden h-1.5 w-12 overflow-hidden rounded-full bg-muted sm:block" aria-hidden>
            <span className={cn('block h-full rounded-full', BAR[state])} style={{ width: `${pct}%` }} />
          </span>
        )}
        {(state === 'near' || state === 'at') && upgradeLink}
      </div>
    );
  }

  return (
    <div data-testid="usage-meter" data-state={state} className={cn('min-w-0', className)}>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate">{name}</span>
        <span className={cn('shrink-0 tabular-nums', TEXT[state])}>{value}</span>
      </div>
      <div
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={name}
        aria-valuemin={0}
        aria-valuemax={unlimited ? undefined : (limit as number)}
        aria-valuenow={used}
      >
        {!unlimited && <div className={cn('h-full rounded-full transition-[width]', BAR[state])} style={{ width: `${pct}%` }} />}
      </div>
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className={cn('min-w-0 truncate text-xs', state === 'ok' || unlimited ? 'text-muted-foreground' : TEXT[state])}>{status}</span>
        {upgradeLink}
      </div>
    </div>
  );
}
